import { log } from 'src/services/app/log.service'

/**
 * Security validation helper functions
 * Centralized location for security-critical validation logic
 */

/**
 * Sanitize request payload for error reporting (PostHog/Slack).
 * Redacts sensitive fields, skips FormData (files can't be serialized).
 * @param {Object|FormData|null} data - Request payload
 * @returns {Object|undefined} - Sanitized payload or undefined if nothing to report
 */
const SENSITIVE_KEYS = ['account_id', 'token', 'text', 'problem_description']

export function sanitizePayload(data, deep = false) {
  if (data === null) return null
  if (data === undefined) return undefined
  if (data instanceof FormData) return 'Form Data'

  let parsed = data
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data)
    } catch {
      return undefined
    }
  }

  if (typeof parsed !== 'object') return parsed
  if (Array.isArray(parsed)) {
    return deep ? parsed.map((item) => sanitizePayload(item, true)) : parsed
  }

  const sanitized = {}
  for (const [key, value] of Object.entries(parsed)) {
    if (SENSITIVE_KEYS.includes(key)) {
      sanitized[key] = _redact(value)
    } else if (deep && value && typeof value === 'object') {
      sanitized[key] = sanitizePayload(value, true)
    } else {
      sanitized[key] = value
    }
  }
  return sanitized
}

function _redact(value) {
  if (typeof value !== 'string' || value.length <= 6) return '[REDACTED]'
  return `${value.slice(0, 3)}...${value.slice(-3)}`
}

/**
 * Validate URL protocol to prevent dangerous protocols
 * @param {string} url - URL to validate
 * @returns {Object} - { isValid: boolean, normalizedUrl: string|null, error: string|null }
 */
export function validateUrlProtocol(url) {
  if (!url || typeof url !== 'string') {
    return {
      isValid: false,
      normalizedUrl: null,
      error: 'URL must be a non-empty string',
    }
  }

  // Normalize URL
  let normalizedUrl = url.trim()

  // Add https:// if no protocol specified
  if (!normalizedUrl.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:/)) {
    normalizedUrl = `https://${normalizedUrl}`
  }

  // Validate protocol using URL parsing
  try {
    const urlObj = new URL(normalizedUrl)
    const allowedProtocols = ['http:', 'https:']

    if (!allowedProtocols.includes(urlObj.protocol)) {
      log.error('validateUrlProtocol: Dangerous protocol blocked', {
        url,
        protocol: urlObj.protocol,
      })
      return {
        isValid: false,
        normalizedUrl: null,
        error: `Protocol ${urlObj.protocol} not allowed. Only http: and https: are permitted.`,
      }
    }

    return {
      isValid: true,
      normalizedUrl,
      error: null,
    }
  } catch (error) {
    log.error('validateUrlProtocol: Invalid URL format', { url, error })
    return {
      isValid: false,
      normalizedUrl: null,
      error: 'Invalid URL format',
    }
  }
}
